import React from 'react';
import FormField from '../shared/FormField';
import formDataStorage from '../../utils/formDataStorage';
import CountrySelect from '../shared/CountrySelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import { Calendar, MapPin, User, ChevronDown, Heart, Plus, Trash2, Users } from 'lucide-react';

// 家庭信息表单
const FamilyInfoForm = ({ data, onChange, fieldErrors = {} }) => {

  // 使用 state 来管理 personal1 数据，确保能响应变化
  const [personal1Data] = React.useState(() => formDataStorage.loadFormData('personal_1'));
  const maritalStatus = personal1Data?.marital_status;

  // 判断是否需要显示配偶信息（已婚、事实婚姻、民事结合、法律分居）
  const needSpouseInfo = ['M', 'C', 'P', 'L'].includes(maritalStatus);

  // 判断是否需要显示前配偶信息（离婚）
  const needFormerSpouseInfo = maritalStatus === 'D';

  // 判断是否需要显示已故配偶信息（丧偶）
  const needDeceasedSpouseInfo = maritalStatus === 'W';

  // 婚姻状况映射
  const MARITAL_STATUS_LABELS = {
    'M': '已婚',
    'C': '事实婚姻',
    'P': '民事结合/家庭伙伴关系',
    'S': '单身',
    'W': '丧偶',
    'D': '离婚',
    'L': '法律分居',
    'O': '其他'
  };
  // 美国身份状态选项
  const US_STATUS_OPTIONS = [
    { value: 'C', label: 'C - 美国公民 (U.S. Citizen)' },
    { value: 'L', label: 'L - 合法永久居民 (Legal Permanent Resident)' },
    { value: 'R', label: 'R - 难民 (Refugee)' },
    { value: 'A', label: 'A - 政治庇护者 (Asylee)' },
    { value: 'S', label: 'S - 其他身份 (Other Status)' },
    { value: 'D', label: 'D - 已故 (Deceased)' },
    { value: 'U', label: 'U - 身份未知 (Unknown Status)' }
  ];

  // 月份映射
  const MONTHS = [
    { value: 'JAN', label: '01月' },
    { value: 'FEB', label: '02月' },
    { value: 'MAR', label: '03月' },
    { value: 'APR', label: '04月' },
    { value: 'MAY', label: '05 ww月' },
    { value: 'JUN', label: '06月' },
    { value: 'JUL', label: '07月' },
    { value: 'AUG', label: '08月' },
    { value: 'SEP', label: '09月' },
    { value: 'OCT', label: '10月' },
    { value: 'NOV', label: '11月' },
    { value: 'DEC', label: '12月' }
  ];

  // 月份英文缩写到数字的映射
  const MONTH_TO_NUMBER = {
    'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
    'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
  };

  // NOTE: 移除未使用的 NUMBER_TO_MONTH 与 mkDate，保留 MONTH_TO_NUMBER 用于 getDaysInMonth

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;

    // 如果月份是英文缩写，转换为数字
    let monthNumber = month;
    if (typeof month === 'string' && MONTH_TO_NUMBER[month]) {
      monthNumber = MONTH_TO_NUMBER[month];
    }

    return new Date(year, monthNumber, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 添加前配偶
  const addFormerSpouse = () => {
    const currentFormerSpouses = data.former_spouses || [];
    const newFormerSpouse = {
      surnames: '',
      given_name: '',
      birth_date: { day: '', month: '', year: '' },
      nationality: '',
      pob_city: '',
      pob_city_unk_ind: false,
      pob_country: '',
      marriage_date: { day: '', month: '', year: '' },
      marriage_end_date: { day: '', month: '', year: '' },
      how_marriage_ended: '',
      marriage_end_country: ''
    };
    onChange('former_spouses', [...currentFormerSpouses, newFormerSpouse]);
  };

  // 删除前配偶
  const removeFormerSpouse = (index) => {
    const currentFormerSpouses = data.former_spouses || [];
    const newFormerSpouses = currentFormerSpouses.filter((_, i) => i !== index);
    onChange('former_spouses', newFormerSpouses);

    // 更新前配偶数量
    onChange('number_of_former_spouses', newFormerSpouses.length.toString());
  };

  // 更新前配偶信息
  const updateFormerSpouse = (index, field, value) => {
    const currentFormerSpouses = data.former_spouses || [];
    const newFormerSpouses = [...currentFormerSpouses];

    if (!newFormerSpouses[index]) {
      newFormerSpouses[index] = {
        surnames: '',
        given_name: '',
        birth_date: { day: '', month: '', year: '' },
        nationality: '',
        pob_city: '',
        pob_city_unk_ind: false,
        pob_country: '',
        marriage_date: { day: '', month: '', year: '' },
        marriage_end_date: { day: '', month: '', year: '' },
        how_marriage_ended: '',
        marriage_end_country: ''
      };
    }

    // 处理日期字段
    if (field.includes('_date.')) {
      const [dateType, dateField] = field.split('.');
      const currentDate = newFormerSpouses[index][dateType] || {};

      // 如果修改了年或月，需要检查日期是否有效
      if (dateField === 'year' || dateField === 'month') {
        const newDate = { ...currentDate, [dateField]: value };
        const maxDays = getDaysInMonth(newDate.year, newDate.month);
        if (newDate.day && parseInt(newDate.day) > maxDays) {
          newDate.day = String(maxDays);
        }
        newFormerSpouses[index] = { ...newFormerSpouses[index], [dateType]: newDate };
      } else {
        newFormerSpouses[index] = {
          ...newFormerSpouses[index],
          [dateType]: { ...currentDate, [dateField]: value }
        };
      }
    } else {
      newFormerSpouses[index] = { ...newFormerSpouses[index], [field]: value };
    }

    onChange('former_spouses', newFormerSpouses);
  };

  // 验证父母出生日期是否早于申请人出生日期
  const validateParentBirthDate = (parentBirthDate, parentType) => {
    if (!parentBirthDate?.year || !parentBirthDate?.month || !parentBirthDate?.day) {
      return '';
    }

    // 获取申请人出生日期
    const personal1Data = formDataStorage.loadFormData('personal_1');
    const applicantBirthDate = personal1Data?.date_and_place_of_birth;
    
    if (!applicantBirthDate?.year || !applicantBirthDate?.month || !applicantBirthDate?.day) {
      return '';
    }

    // 转换月份格式（如果需要）
    let parentMonth = parentBirthDate.month;
    let applicantMonth = applicantBirthDate.month;
    
    if (typeof parentMonth === 'string' && MONTH_TO_NUMBER[parentMonth]) {
      parentMonth = MONTH_TO_NUMBER[parentMonth];
    }
    if (typeof applicantMonth === 'string' && MONTH_TO_NUMBER[applicantMonth]) {
      applicantMonth = MONTH_TO_NUMBER[applicantMonth];
    }

    // 创建日期对象进行比较
    const parentDate = new Date(parseInt(parentBirthDate.year), parentMonth - 1, parseInt(parentBirthDate.day));
    const applicantDate = new Date(parseInt(applicantBirthDate.year), applicantMonth - 1, parseInt(applicantBirthDate.day));

    if (parentDate >= applicantDate) {
      return `${parentType}出生日期不能晚于或等于申请人出生日期`;
    }

    return '';
  };

  // 更新父亲信息
  const updateFatherInfo = (field, value) => {
    if (field.startsWith('father_birth_date.')) {
      const dateField = field.split('.')[1];
      const currentBirthDate = data.father_birth_date || {};

      // 如果修改了年或月，需要检查日期是否有效
      if (dateField === 'year' || dateField === 'month') {
        const newBirthDate = { ...currentBirthDate, [dateField]: value };
        const maxDays = getDaysInMonth(newBirthDate.year, newBirthDate.month);
        if (newBirthDate.day && parseInt(newBirthDate.day) > maxDays) {
          newBirthDate.day = String(maxDays);
        }
        onChange('father_birth_date', newBirthDate);
      } else {
        onChange('father_birth_date', { ...currentBirthDate, [dateField]: value });
      }
    } else {
      onChange(field, value);
    }
  };

  // 更新母亲信息
  const updateMotherInfo = (field, value) => {
    if (field.startsWith('mother_birth_date.')) {
      const dateField = field.split('.')[1];
      const currentBirthDate = data.mother_birth_date || {};

      // 如果修改了年或月，需要检查日期是否有效
      if (dateField === 'year' || dateField === 'month') {
        const newBirthDate = { ...currentBirthDate, [dateField]: value };
        const maxDays = getDaysInMonth(newBirthDate.year, newBirthDate.month);
        if (newBirthDate.day && parseInt(newBirthDate.day) > maxDays) {
          newBirthDate.day = String(maxDays);
        }
        onChange('mother_birth_date', newBirthDate);
      } else {
        onChange('mother_birth_date', { ...currentBirthDate, [dateField]: value });
      }
    } else {
      onChange(field, value);
    }
  };

  // 更新配偶信息
  const updateSpouseInfo = (field, value) => {
    if (field.startsWith('spouse_birth_date.')) {
      const dateField = field.split('.')[1];
      const currentBirthDate = data.spouse_birth_date || {};

      // 如果修改了年或月，需要检查日期是否有效
      if (dateField === 'year' || dateField === 'month') {
        const newBirthDate = { ...currentBirthDate, [dateField]: value };
        const maxDays = getDaysInMonth(newBirthDate.year, newBirthDate.month);
        if (newBirthDate.day && parseInt(newBirthDate.day) > maxDays) {
          newBirthDate.day = String(maxDays);
        }
        onChange('spouse_birth_date', newBirthDate);
      } else {
        onChange('spouse_birth_date', { ...currentBirthDate, [dateField]: value });
      }
    } else {
      onChange(field, value);
    }
  };

  // 更新已故配偶信息
  const updateDeceasedSpouseInfo = (field, value) => {
    if (field.startsWith('deceased_spouse_birth_date.')) {
      const dateField = field.split('.')[1];
      const currentBirthDate = data.deceased_spouse_birth_date || {};

      // 如果修改了年或月，需要检查日期是否有效
      if (dateField === 'year' || dateField === 'month') {
        const newBirthDate = { ...currentBirthDate, [dateField]: value };
        const maxDays = getDaysInMonth(newBirthDate.year, newBirthDate.month);
        if (newBirthDate.day && parseInt(newBirthDate.day) > maxDays) {
          newBirthDate.day = String(maxDays);
        }
        onChange('deceased_spouse_birth_date', newBirthDate);
      } else {
        onChange('deceased_spouse_birth_date', { ...currentBirthDate, [dateField]: value });
      }
    } else {
      onChange(field, value);
    }
  };

  // 获取身份状态显示名称
  const getStatusLabel = (code) => {
    const option = US_STATUS_OPTIONS.find(opt => opt.value === code);
    return option ? option.label : code;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-pink-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">👨‍👩‍👦</span>
          家庭信息
        </h3>

        {/* 婚姻状况信息显示 */}
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
          <h4 className="text-md font-semibold text-blue-800 mb-2 flex items-center">
            <Heart className="w-4 h-4 mr-2" />
            根据个人信息中的婚姻状况自动调整显示内容
          </h4>

          {maritalStatus ? (
            <div className="text-sm text-blue-700">
              <strong>当前婚姻状况：</strong>
              <span className="ml-2 px-3 py-1 bg-blue-100 rounded-full font-medium">
                {MARITAL_STATUS_LABELS[maritalStatus] || maritalStatus}
              </span>
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              <span className="inline-flex items-center px-3 py-1 bg-yellow-100 rounded-full font-medium text-yellow-800">
                ⏳ 等待个人信息页面的婚姻状况数据...
              </span>
              <div className="mt-2 text-xs text-gray-500">
                请先在"个人信息1"页面选择您的婚姻状况，然后返回此页面查看相应的表单内容。
              </div>
            </div>
          )}

          {maritalStatus && (
            <>
              {needSpouseInfo && (
                <div className="mt-2 text-sm text-blue-600">✅ 将显示配偶信息填写区域</div>
              )}
              {needFormerSpouseInfo && (
                <div className="mt-2 text-sm text-orange-600">✅ 将显示前配偶信息填写区域</div>
              )}
              {needDeceasedSpouseInfo && (
                <div className="mt-2 text-sm text-purple-600">✅ 将显示已故配偶信息填写区域</div>
              )}
              {!needSpouseInfo && !needFormerSpouseInfo && !needDeceasedSpouseInfo && (
                <div className="mt-2 text-sm text-gray-600">ℹ️ 根据当前婚姻状况，无需填写配偶信息</div>
              )}
            </>
          )}
        </div>

        {/* 父亲信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <User className="w-5 h-5 mr-2" />
            父亲信息
          </h4>

          <div className="space-y-4">
            {/* 父亲姓名 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    父亲姓氏 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-3 py-1 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.father_surnames_unk_ind || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateFatherInfo('father_surnames_unk_ind', checked);
                          if (checked) {
                            updateFatherInfo('father_surnames', '');
                          }
                        }}
                        className="mr-2 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-xs font-medium text-gray-700">不知道</span>
                    </label>
                  </div>
                </div>
                <FormField
                  label=""
                  value={data.father_surnames || ''}
                  onChange={(e) => updateFatherInfo('father_surnames', e.target.value)}
                  placeholder="ZHANG (例如：Hernandez Garcia)"
                  disabled={data.father_surnames_unk_ind}
                  required={!data.father_surnames_unk_ind}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    父亲名字 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-3 py-1 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.father_given_name_unk_ind || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateFatherInfo('father_given_name_unk_ind', checked);
                          if (checked) {
                            updateFatherInfo('father_given_name', '');
                          }
                        }}
                        className="mr-2 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-xs font-medium text-gray-700">不知道</span>
                    </label>
                  </div>
                </div>
                <FormField
                  label=""
                  value={data.father_given_name || ''}
                  onChange={(e) => updateFatherInfo('father_given_name', e.target.value)}
                  placeholder="MING (例如：Juan Miguel)"
                  disabled={data.father_given_name_unk_ind}
                  required={!data.father_given_name_unk_ind}
                />
              </div>
            </div>

            {/* 父亲出生日期 */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700 flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  父亲出生日期
                </h5>
                <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.father_dob_unk_ind || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateFatherInfo('father_dob_unk_ind', checked);
                        if (checked) {
                          onChange('father_birth_date', { day: '', month: '', year: '' });
                        }
                      }}
                      className="mr-2 w-5 h-5 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">出生日期未知</span>
                  </label>
                </div>
              </div>

              {!data.father_dob_unk_ind && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                    <input
                      type="number"
                      min="1900"
                      max="2030"
                      placeholder="1990"
                      value={(data.father_birth_date && data.father_birth_date.year) || ''}
                      onChange={(e) => updateFatherInfo('father_birth_date.year', e.target.value)}
                      className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                    <div className="relative">
                      <select
                        value={(data.father_birth_date && data.father_birth_date.month) || ''}
                        onChange={(e) => updateFatherInfo('father_birth_date.month', e.target.value)}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                      >
                        <option value="">选择月份</option>
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                    <div className="relative">
                      <select
                        value={(data.father_birth_date && data.father_birth_date.day) || ''}
                        onChange={(e) => updateFatherInfo('father_birth_date.day', e.target.value)}
                        disabled={!data.father_birth_date?.year || !data.father_birth_date?.month}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <option value="">选择日期</option>
                        {generateDayOptions(data.father_birth_date?.year, data.father_birth_date?.month).map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 父亲出生日期验证错误提示 */}
              {!data.father_dob_unk_ind && (() => {
                const validationError = validateParentBirthDate(data.father_birth_date, '父亲');
                if (validationError) {
                  return (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start">
                        <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                        <p className="text-sm text-red-800 font-medium">{validationError}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {data.father_dob_unk_ind && (
                <div className="p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
                  <p className="text-sm text-orange-700">
                    <strong>注意：</strong> 已选择出生日期未知，日期选择器将被禁用。
                  </p>
                </div>
              )}
            </div>

            {/* 父亲美国居住状态 */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  父亲是否居住在美国？
                </h5>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                    <input
                      type="radio"
                      name="father_live_in_us_ind"
                      value="false"
                      checked={data.father_live_in_us_ind === false}
                      onChange={(e) => {
                        updateFatherInfo('father_live_in_us_ind', false);
                        updateFatherInfo('if_father_in_us_status', '');
                      }}
                      className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">❌ 否 - 不居住在美国</span>
                  </label>

                  <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                    <input
                      type="radio"
                      name="father_live_in_us_ind"
                      value="true"
                      checked={data.father_live_in_us_ind === true}
                      onChange={(e) => {
                        updateFatherInfo('father_live_in_us_ind', true);
                      }}
                      className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">✅ 是 - 居住在美国</span>
                  </label>
                </div>
              </div>

              {data.father_live_in_us_ind && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    父亲在美国的身份状态 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={data.if_father_in_us_status || ''}
                      onChange={(e) => updateFatherInfo('if_father_in_us_status', e.target.value)}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gradient-to-r from-white to-green-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="">请选择身份状态...</option>
                      {US_STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>

                  {data.if_father_in_us_status && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
                      <div className="text-sm font-medium text-green-800">
                        ✅ 已选择身份: {getStatusLabel(data.if_father_in_us_status)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 母亲信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <User className="w-5 h-5 mr-2" />
            母亲信息
          </h4>

          <div className="space-y-4">
            {/* 母亲姓名 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    母亲姓氏 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-3 py-1 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.mother_surnames_unk_ind || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateMotherInfo('mother_surnames_unk_ind', checked);
                          if (checked) {
                            updateMotherInfo('mother_surnames', '');
                          }
                        }}
                        className="mr-2 w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-xs font-medium text-gray-700">不知道</span>
                    </label>
                  </div>
                </div>
                <FormField
                  label=""
                  value={data.mother_surnames || ''}
                  onChange={(e) => updateMotherInfo('mother_surnames', e.target.value)}
                  placeholder="WANG (例如：Hernandez Garcia)"
                  disabled={data.mother_surnames_unk_ind}
                  required={!data.mother_surnames_unk_ind}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    母亲名字 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center px-3 py-1 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.mother_given_name_unk_ind || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateMotherInfo('mother_given_name_unk_ind', checked);
                          if (checked) {
                            updateMotherInfo('mother_given_name', '');
                          }
                        }}
                        className="mr-2 w-4 h-4 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-xs font-medium text-gray-700">不知道</span>
                    </label>
                  </div>
                </div>
                <FormField
                  label=""
                  value={data.mother_given_name || ''}
                  onChange={(e) => updateMotherInfo('mother_given_name', e.target.value)}
                  placeholder="MEI (例如：Juanita Miguel)"
                  disabled={data.mother_given_name_unk_ind}
                  required={!data.mother_given_name_unk_ind}
                />
              </div>
            </div>

            {/* 母亲出生日期 */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700 flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  母亲出生日期
                </h5>
                <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={data.mother_dob_unk_ind || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        updateMotherInfo('mother_dob_unk_ind', checked);
                        if (checked) {
                          onChange('mother_birth_date', { day: '', month: '', year: '' });
                        }
                      }}
                      className="mr-2 w-5 h-5 text-purple-600 bg-white border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">出生日期未知</span>
                  </label>
                </div>
              </div>

              {!data.mother_dob_unk_ind && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                    <input
                      type="number"
                      min="1900"
                      max="2030"
                      placeholder="1990"
                      value={(data.mother_birth_date && data.mother_birth_date.year) || ''}
                      onChange={(e) => updateMotherInfo('mother_birth_date.year', e.target.value)}
                      className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                    <div className="relative">
                      <select
                        value={(data.mother_birth_date && data.mother_birth_date.month) || ''}
                        onChange={(e) => updateMotherInfo('mother_birth_date.month', e.target.value)}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                      >
                        <option value="">选择月份</option>
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                    <div className="relative">
                      <select
                        value={(data.mother_birth_date && data.mother_birth_date.day) || ''}
                        onChange={(e) => updateMotherInfo('mother_birth_date.day', e.target.value)}
                        disabled={!data.mother_birth_date?.year || !data.mother_birth_date?.month}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <option value="">选择日期</option>
                        {generateDayOptions(data.mother_birth_date?.year, data.mother_birth_date?.month).map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 母亲出生日期验证错误提示 */}
              {!data.mother_dob_unk_ind && (() => {
                const validationError = validateParentBirthDate(data.mother_birth_date, '母亲');
                if (validationError) {
                  return (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start">
                        <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                        <p className="text-sm text-red-800 font-medium">{validationError}</p>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {data.mother_dob_unk_ind && (
                <div className="p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
                  <p className="text-sm text-orange-700">
                    <strong>注意：</strong> 已选择出生日期未知，日期选择器将被禁用。
                  </p>
                </div>
              )}
            </div>

            {/* 母亲美国居住状态 */}
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-l-4 border-indigo-400">
              <div className="mb-4">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  母亲是否居住在美国？
                </h5>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                    <input
                      type="radio"
                      name="mother_live_in_us_ind"
                      value="false"
                      checked={data.mother_live_in_us_ind === false}
                      onChange={(e) => {
                        updateMotherInfo('mother_live_in_us_ind', false);
                        updateMotherInfo('if_mother_in_us_status', '');
                      }}
                      className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">❌ 否 - 不居住在美国</span>
                  </label>

                  <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                    <input
                      type="radio"
                      name="mother_live_in_us_ind"
                      value="true"
                      checked={data.mother_live_in_us_ind === true}
                      onChange={(e) => {
                        updateMotherInfo('mother_live_in_us_ind', true);
                      }}
                      className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-base font-medium text-gray-700">✅ 是 - 居住在美国</span>
                  </label>
                </div>
              </div>

              {data.mother_live_in_us_ind && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    母亲在美国的身份状态 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={data.if_mother_in_us_status || ''}
                      onChange={(e) => updateMotherInfo('if_mother_in_us_status', e.target.value)}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gradient-to-r from-white to-indigo-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="">请选择身份状态...</option>
                      {US_STATUS_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>

                  {data.if_mother_in_us_status && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border-l-4 border-indigo-400">
                      <div className="text-sm font-medium text-indigo-800">
                        ✅ 已选择身份: {getStatusLabel(data.if_mother_in_us_status)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 其他美国亲属信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Users className="w-5 h-5 mr-2" />
            其他美国亲属信息
          </h4>

          <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border-l-4 border-emerald-400">
            <div className="mb-4">
              <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                <Users className="w-4 h-4 mr-2" />
                除父母以外，您在美国是否还有其他直系亲属？
              </h5>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="us_immed_relative_ind"
                    value="false"
                    checked={data.us_immed_relative_ind === false}
                    onChange={(e) => {
                      onChange('us_immed_relative_ind', false);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 没有其他直系亲属</span>
                </label>

                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="us_immed_relative_ind"
                    value="true"
                    checked={data.us_immed_relative_ind === true}
                    onChange={(e) => {
                      onChange('us_immed_relative_ind', true);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 有其他直系亲属</span>
                </label>
              </div>
            </div>

            {/* 帮助信息 */}
            <div className="p-4 bg-emerald-100 rounded-lg border-l-4 border-emerald-500">
              <h6 className="text-sm font-semibold text-emerald-800 mb-2">
                💡 帮助：直系亲属定义
              </h6>
              <p className="text-sm text-emerald-700">
                <strong>直系亲属包括：</strong> 未婚夫/妻、配偶（丈夫/妻子）、子女（儿子/女儿）、或兄弟姐妹（兄弟/姐妹）。
              </p>
              <p className="text-xs text-emerald-600 mt-2">
                <strong>英文原文：</strong> Immediate Relatives means fiancé/fiancée, spouse (husband/wife), child (son/daughter), or sibling (brother/sister).
              </p>
            </div>

            {/* 选择结果提示 */}
            {data.us_immed_relative_ind !== undefined && (
              <div className="mt-3 p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border-l-4 border-emerald-400">
                <div className="text-sm font-medium text-emerald-800">
                  {data.us_immed_relative_ind ? (
                    '✅ 已选择：是 - 有其他直系亲属在美国'
                  ) : (
                    '✅ 已选择：否 - 没有其他直系亲属在美国'
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 其他亲属信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
            <div className="mb-4">
              <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                <Users className="w-4 h-4 mr-2" />
                您在美国是否还有其他亲属？
              </h5>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="us_other_relative_ind"
                    value="false"
                    checked={data.us_other_relative_ind === false}
                    onChange={(e) => {
                      onChange('us_other_relative_ind', false);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 没有其他亲属</span>
                </label>

                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="us_other_relative_ind"
                    value="true"
                    checked={data.us_other_relative_ind === true}
                    onChange={(e) => {
                      onChange('us_other_relative_ind', true);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 有其他亲属</span>
                </label>
              </div>
            </div>

            {/* 帮助信息 */}
            <div className="p-4 bg-blue-100 rounded-lg border-l-4 border-blue-500">
              <h6 className="text-sm font-semibold text-blue-800 mb-2">
                💡 帮助：其他亲属定义
              </h6>
              <p className="text-sm text-blue-700">
                <strong>其他亲属包括：</strong> 除了直系亲属之外的所有其他亲属，如堂兄弟姐妹、表兄弟姐妹、叔叔阿姨、祖父母等。
              </p>
            </div>

            {/* 选择结果提示 */}
            {data.us_other_relative_ind !== undefined && (
              <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
                <div className="text-sm font-medium text-blue-800">
                  {data.us_other_relative_ind ? (
                    '✅ 已选择：是 - 有其他亲属在美国'
                  ) : (
                    '✅ 已选择：否 - 没有其他亲属在美国'
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 配偶信息 - 仅在已婚时显示 */}
        {needSpouseInfo && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Heart className="w-5 h-5 mr-2" />
              配偶信息
              <span className="ml-2 text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">
                {MARITAL_STATUS_LABELS[maritalStatus]}
              </span>
            </h4>

            <div className="space-y-4">
              {/* 配偶姓名 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="配偶姓氏"
                  value={data.spouse_surnames || ''}
                  onChange={(e) => updateSpouseInfo('spouse_surnames', e.target.value)}
                  placeholder="SMITH"
                  required
                />
                <FormField
                  label="配偶名字"
                  value={data.spouse_given_name || ''}
                  onChange={(e) => updateSpouseInfo('spouse_given_name', e.target.value)}
                  placeholder="JOHN"
                  required
                />
              </div>

              {/* 配偶出生日期 */}
              <div className="p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border-l-4 border-rose-400">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-md font-semibold text-gray-700 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    配偶出生日期
                  </h5>
                  <div className="flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.spouse_dob_unk_ind || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateSpouseInfo('spouse_dob_unk_ind', checked);
                          if (checked) {
                            onChange('spouse_birth_date', { day: '', month: '', year: '' });
                          }
                        }}
                        className="mr-2 w-5 h-5 text-rose-600 bg-white border-gray-300 rounded focus:ring-rose-500 focus:ring-2"
                      />
                      <span className="text-base font-medium text-gray-700">出生日期未知</span>
                    </label>
                  </div>
                </div>

                {!data.spouse_dob_unk_ind && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                      <input
                        type="number"
                        min="1900"
                        max="2030"
                        placeholder="1990"
                        value={(data.spouse_birth_date && data.spouse_birth_date.year) || ''}
                        onChange={(e) => updateSpouseInfo('spouse_birth_date.year', e.target.value)}
                        className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent text-center bg-gradient-to-r from-white to-rose-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                      <div className="relative">
                        <select
                          value={(data.spouse_birth_date && data.spouse_birth_date.month) || ''}
                          onChange={(e) => updateSpouseInfo('spouse_birth_date.month', e.target.value)}
                          className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gradient-to-r from-white to-rose-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                        >
                          <option value="">选择月份</option>
                          {MONTHS.map(month => (
                            <option key={month.value} value={month.value}>
                              {month.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                      <div className="relative">
                        <select
                          value={(data.spouse_birth_date && data.spouse_birth_date.day) || ''}
                          onChange={(e) => updateSpouseInfo('spouse_birth_date.day', e.target.value)}
                          disabled={!data.spouse_birth_date?.year || !data.spouse_birth_date?.month}
                          className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gradient-to-r from-white to-rose-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                          <option value="">选择日期</option>
                          {generateDayOptions(data.spouse_birth_date?.year, data.spouse_birth_date?.month).map(day => (
                            <option key={day.value} value={day.value}>
                              {day.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {data.spouse_dob_unk_ind && (
                  <div className="p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
                    <p className="text-sm text-orange-700">
                      <strong>注意：</strong> 已选择出生日期未知，日期选择器将被禁用。
                    </p>
                  </div>
                )}
              </div>

              {/* 配偶国籍 */}
              <div>
                <CountrySelect
                  label="配偶国籍"
                  value={data.spouse_nationality || ''}
                  onChange={(value) => updateSpouseInfo('spouse_nationality', value)}
                  required
                />
              </div>

              {/* 配偶出生地 */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border-l-4 border-amber-400">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  配偶出生地
                </h5>

                {/* 出生城市 */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      出生城市
                    </label>
                    <div className="flex items-center px-3 py-1 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={data.spouse_pob_city_unk_ind || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            updateSpouseInfo('spouse_pob_city_unk_ind', checked);
                            if (checked) {
                              updateSpouseInfo('spouse_pob_city', '');
                            }
                          }}
                          className="mr-2 w-4 h-4 text-amber-600 bg-white border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                        />
                        <span className="text-xs font-medium text-gray-700">不知道</span>
                      </label>
                    </div>
                  </div>
                  <FormField
                    label=""
                    value={data.spouse_pob_city || ''}
                    onChange={(e) => updateSpouseInfo('spouse_pob_city', e.target.value)}
                    placeholder="请输入出生城市"
                    disabled={data.spouse_pob_city_unk_ind}
                  />
                </div>

                {/* 出生国家 */}
                <div>
                  <CountrySelect
                    label="出生国家/地区"
                    value={data.spouse_pob_country || ''}
                    onChange={(value) => updateSpouseInfo('spouse_pob_country', value)}
                    required
                  />
                </div>
              </div>

              {/* 配偶地址 */}
              <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-l-4 border-cyan-400">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  配偶地址
                </h5>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    配偶地址类型 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={data.spouse_address_type || ''}
                      onChange={(e) => {
                        updateSpouseInfo('spouse_address_type', e.target.value);
                        // 如果不是"其他"选项，清空具体地址
                        if (e.target.value !== 'O') {
                          updateSpouseInfo('spouse_address', {
                            street_address: '',
                            street_address_2: '',
                            city: '',
                            state: '',
                            zip_code: '',
                            country: ''
                          });
                        }
                      }}
                      className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-gradient-to-r from-white to-cyan-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="">请选择地址类型...</option>
                      <option value="H">与家庭住址相同</option>
                      <option value="M">与邮寄地址相同</option>
                      <option value="U">与美国联系人地址相同</option>
                      <option value="D">不知道</option>
                      <option value="O">其他（请指定地址）</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* 当选择"其他"时显示具体地址输入 */}
                {data.spouse_address_type === 'O' && (
                  <div className="space-y-3 mt-4 p-3 bg-white rounded-lg border border-cyan-200">
                    <h6 className="text-sm font-semibold text-cyan-800 mb-3">请填写具体地址：</h6>

                    <FormField
                      label="街道地址 1"
                      value={(data.spouse_address && data.spouse_address.street_address) || ''}
                      onChange={(e) => updateSpouseInfo('spouse_address', {
                        ...(data.spouse_address || {}),
                        street_address: e.target.value
                      })}
                      placeholder="请输入街道地址"
                      required
                    />

                    <FormField
                      label="街道地址 2"
                      value={(data.spouse_address && data.spouse_address.street_address_2) || ''}
                      onChange={(e) => updateSpouseInfo('spouse_address', {
                        ...(data.spouse_address || {}),
                        street_address_2: e.target.value
                      })}
                      placeholder="公寓号、套房号等（可选）"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormField
                        label="城市"
                        value={(data.spouse_address && data.spouse_address.city) || ''}
                        onChange={(e) => updateSpouseInfo('spouse_address', {
                          ...(data.spouse_address || {}),
                          city: e.target.value
                        })}
                        placeholder="请输入城市"
                        required
                      />

                      <FormField
                        label="州/省"
                        value={(data.spouse_address && data.spouse_address.state) || ''}
                        onChange={(e) => updateSpouseInfo('spouse_address', {
                          ...(data.spouse_address || {}),
                          state: e.target.value
                        })}
                        placeholder="请输入州/省"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <ZIPCodeInput
                        label="邮政编码"
                        value={(data.spouse_address && data.spouse_address.zip_code) || ''}
                        onChange={(value) => updateSpouseInfo('spouse_address', {
                          ...(data.spouse_address || {}),
                          zip_code: value
                        })}
                      />

                      <div>
                        <CountrySelect
                          label="国家"
                          value={(data.spouse_address && data.spouse_address.country) || ''}
                          onChange={(value) => updateSpouseInfo('spouse_address', {
                            ...(data.spouse_address || {}),
                            country: value
                          })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* 地址类型选择的说明 */}
                {data.spouse_address_type && data.spouse_address_type !== 'O' && (
                  <div className="mt-3 p-3 bg-cyan-50 rounded-lg border-l-4 border-cyan-400">
                    <div className="text-sm text-cyan-700">
                      ✅ 已选择: {
                        {
                          'H': '与家庭住址相同',
                          'M': '与邮寄地址相同',
                          'U': '与美国联系人地址相同',
                          'D': '不知道'
                        }[data.spouse_address_type] || data.spouse_address_type
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* 配偶美国居住状态 */}
              <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border-l-4 border-pink-400">
                <div className="mb-4">
                  <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                    <MapPin className="w-4 h-4 mr-2" />
                    配偶是否居住在美国？
                  </h5>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="spouse_live_in_us_ind"
                        value="false"
                        checked={data.spouse_live_in_us_ind === false}
                        onChange={(e) => {
                          updateSpouseInfo('spouse_live_in_us_ind', false);
                          updateSpouseInfo('if_spouse_in_us_status', '');
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-base font-medium text-gray-700">❌ 否 - 不居住在美国</span>
                    </label>

                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="spouse_live_in_us_ind"
                        value="true"
                        checked={data.spouse_live_in_us_ind === true}
                        onChange={(e) => {
                          updateSpouseInfo('spouse_live_in_us_ind', true);
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-base font-medium text-gray-700">✅ 是 - 居住在美国</span>
                    </label>
                  </div>
                </div>

                {data.spouse_live_in_us_ind && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      配偶在美国的身份状态 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        value={data.if_spouse_in_us_status || ''}
                        onChange={(e) => updateSpouseInfo('if_spouse_in_us_status', e.target.value)}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-gradient-to-r from-white to-pink-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                      >
                        <option value="">请选择身份状态...</option>
                        {US_STATUS_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {data.if_spouse_in_us_status && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border-l-4 border-pink-400">
                        <div className="text-sm font-medium text-pink-800">
                          ✅ 已选择身份: {getStatusLabel(data.if_spouse_in_us_status)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 前配偶信息 - 仅在离婚时显示 */}
        {needFormerSpouseInfo && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Users className="w-5 h-5 mr-2" />
              前配偶信息
              <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                {MARITAL_STATUS_LABELS[maritalStatus]}
              </span>
            </h4>

            {/* 前配偶数量 */}
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-400">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  前配偶数量 <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => {
                    addFormerSpouse();
                    const currentCount = data.former_spouses ? data.former_spouses.length : 0;
                    onChange('number_of_former_spouses', (currentCount + 1).toString());
                  }}
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加前配偶
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  label=""
                  value={data.number_of_former_spouses || ''}
                  onChange={(e) => onChange('number_of_former_spouses', e.target.value)}
                  placeholder="输入前配偶数量"
                  type="number"
                  min="0"
                  max="10"
                  required
                />
              </div>

              {data.number_of_former_spouses && parseInt(data.number_of_former_spouses) > 0 && (
                <div className="mt-3 p-3 bg-orange-100 rounded-lg border-l-4 border-orange-400">
                  <p className="text-sm text-orange-700">
                    <strong>提示：</strong> 已设置前配偶数量为 {data.number_of_former_spouses} 人，请在下方填写每位前配偶的详细信息。
                  </p>
                </div>
              )}
            </div>

            {/* 前配偶列表 */}
            {data.former_spouses && data.former_spouses.length > 0 && (
              <div className="space-y-6">
                <h5 className="text-md font-semibold text-gray-800 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  前配偶详细信息
                </h5>

                {data.former_spouses.map((formerSpouse, index) => (
                  <div key={index} className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-l-4 border-orange-400 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h6 className="text-lg font-semibold text-orange-800">
                        前配偶 #{index + 1}
                      </h6>
                      {data.former_spouses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFormerSpouse(index)}
                          className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                          title="删除此前配偶"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      {/* 姓名 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          label="姓氏 (Surnames)"
                          value={formerSpouse.surnames || ''}
                          onChange={(e) => updateFormerSpouse(index, 'surnames', e.target.value)}
                          placeholder="SMITH"
                          required
                        />
                        <FormField
                          label="名字 (Given Names)"
                          value={formerSpouse.given_name || ''}
                          onChange={(e) => updateFormerSpouse(index, 'given_name', e.target.value)}
                          placeholder="JOHN"
                          required
                        />
                      </div>

                      {/* 出生日期 */}
                      <div className="p-4 bg-white rounded-lg border border-orange-200">
                        <h6 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                          <Calendar className="w-4 h-4 mr-2" />
                          出生日期
                        </h6>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                            <div className="relative">
                              <select
                                value={(formerSpouse.birth_date && formerSpouse.birth_date.day) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'birth_date.day', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {Array.from({ length: 31 }, (_, i) => {
                                  const day = String(i + 1).padStart(2, '0');
                                  return (
                                    <option key={day} value={day}>{day}</option>
                                  );
                                })}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                            <div className="relative">
                              <select
                                value={(formerSpouse.birth_date && formerSpouse.birth_date.month) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'birth_date.month', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {MONTHS.map(month => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                            <input
                              type="number"
                              min="1900"
                              max="2030"
                              placeholder="1990"
                              value={(formerSpouse.birth_date && formerSpouse.birth_date.year) || ''}
                              onChange={(e) => updateFormerSpouse(index, 'birth_date.year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 国籍 */}
                      <div>
                        <CountrySelect
                          label="国籍 (Country/Region of Origin)"
                          value={formerSpouse.nationality || ''}
                          onChange={(value) => updateFormerSpouse(index, 'nationality', value)}
                          required
                        />
                      </div>

                      {/* 出生地 */}
                      <div className="p-4 bg-white rounded-lg border border-orange-200">
                        <h6 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                          <MapPin className="w-4 h-4 mr-2" />
                          出生地
                        </h6>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormField
                              label="出生城市"
                              value={formerSpouse.pob_city || ''}
                              onChange={(e) => updateFormerSpouse(index, 'pob_city', e.target.value)}
                              placeholder="请输入出生城市"
                              disabled={formerSpouse.pob_city_unk_ind}
                            />
                            <div className="ml-4 flex items-center px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg hover:bg-orange-100 transition-colors">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formerSpouse.pob_city_unk_ind || false}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    updateFormerSpouse(index, 'pob_city_unk_ind', checked);
                                    if (checked) {
                                      updateFormerSpouse(index, 'pob_city', '');
                                    }
                                  }}
                                  className="mr-2 w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                                />
                                <span className="text-xs font-medium text-gray-700">不知道</span>
                              </label>
                            </div>
                          </div>
                          <CountrySelect
                            label="出生国家/地区"
                            value={formerSpouse.pob_country || ''}
                            onChange={(value) => updateFormerSpouse(index, 'pob_country', value)}
                            required
                          />
                        </div>
                      </div>

                      {/* 结婚日期 */}
                      <div className="p-4 bg-white rounded-lg border border-orange-200">
                        <h6 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                          <Calendar className="w-4 h-4 mr-2" />
                          结婚日期
                        </h6>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                            <div className="relative">
                              <select
                                id={`former_spouse_${index}_marriage_date_day`}
                                value={(formerSpouse.marriage_date && formerSpouse.marriage_date.day) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'marriage_date.day', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {Array.from({ length: 31 }, (_, i) => {
                                  const day = String(i + 1).padStart(2, '0');
                                  return (
                                    <option key={day} value={day}>{day}</option>
                                  );
                                })}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                            <div className="relative">
                              <select
                                id={`former_spouse_${index}_marriage_date_month`}
                                value={(formerSpouse.marriage_date && formerSpouse.marriage_date.month) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'marriage_date.month', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {MONTHS.map(month => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                            <input
                              id={`former_spouse_${index}_marriage_date_year`}
                              type="number"
                              min="1900"
                              max="2030"
                              placeholder="2000"
                              value={(formerSpouse.marriage_date && formerSpouse.marriage_date.year) || ''}
                              onChange={(e) => updateFormerSpouse(index, 'marriage_date.year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 婚姻结束日期 */}
                      <div className="p-4 bg-white rounded-lg border border-orange-200">
                        <h6 className="text-sm font-semibold text-gray-700 flex items-center mb-3">
                          <Calendar className="w-4 h-4 mr-2" />
                          婚姻结束日期
                        </h6>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                            <div className="relative">
                              <select
                                id={`former_spouse_${index}_marriage_end_date_day`}
                                value={(formerSpouse.marriage_end_date && formerSpouse.marriage_end_date.day) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'marriage_end_date.day', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {Array.from({ length: 31 }, (_, i) => {
                                  const day = String(i + 1).padStart(2, '0');
                                  return (
                                    <option key={day} value={day}>{day}</option>
                                  );
                                })}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                            <div className="relative">
                              <select
                                id={`former_spouse_${index}_marriage_end_date_month`}
                                value={(formerSpouse.marriage_end_date && formerSpouse.marriage_end_date.month) || ''}
                                onChange={(e) => updateFormerSpouse(index, 'marriage_end_date.month', e.target.value)}
                                className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer text-sm"
                              >
                                <option value="">选择</option>
                                {MONTHS.map(month => (
                                  <option key={month.value} value={month.value}>
                                    {month.label}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-2 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                            <input
                              id={`former_spouse_${index}_marriage_end_date_year`}
                              type="number"
                              min="1900"
                              max="2030"
                              placeholder="2020"
                              value={(formerSpouse.marriage_end_date && formerSpouse.marriage_end_date.year) || ''}
                              onChange={(e) => updateFormerSpouse(index, 'marriage_end_date.year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-center bg-white shadow-sm hover:shadow-md transition-all duration-200 text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* 婚姻如何结束 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          婚姻如何结束 <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={formerSpouse.how_marriage_ended || ''}
                          onChange={(e) => updateFormerSpouse(index, 'how_marriage_ended', e.target.value)}
                          placeholder="请详细描述婚姻是如何结束的..."
                          rows={3}
                          maxLength={4000}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white shadow-sm hover:shadow-md transition-all duration-200 resize-vertical"
                          required
                        />
                        <div className="mt-1 text-xs text-gray-500">
                          剩余字符数: {4000 - (formerSpouse.how_marriage_ended || '').length}
                        </div>
                      </div>

                      {/* 婚姻结束的国家 */}
                      <div>
                        <CountrySelect
                          label="婚姻结束的国家/地区"
                          value={formerSpouse.marriage_end_country || ''}
                          onChange={(value) => updateFormerSpouse(index, 'marriage_end_country', value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 已故配偶信息 - 仅在丧偶时显示 */}
        {needDeceasedSpouseInfo && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Heart className="w-5 h-5 mr-2" />
              已故配偶信息
              <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                {MARITAL_STATUS_LABELS[maritalStatus]}
              </span>
            </h4>

            <div className="space-y-4">
              {/* 已故配偶姓名 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="已故配偶姓氏"
                  value={data.deceased_spouse_surnames || ''}
                  onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_surnames', e.target.value)}
                  placeholder="SMITH"
                  required
                />
                <FormField
                  label="已故配偶名字"
                  value={data.deceased_spouse_given_name || ''}
                  onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_given_name', e.target.value)}
                  placeholder="JOHN"
                  required
                />
              </div>

              {/* 已故配偶出生日期 */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <Calendar className="w-4 h-4 mr-2" />
                  已故配偶出生日期
                </h5>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                    <input
                      type="number"
                      min="1900"
                      max="2030"
                      placeholder="1980"
                      value={(data.deceased_spouse_birth_date && data.deceased_spouse_birth_date.year) || ''}
                      onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_birth_date.year', e.target.value)}
                      className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                    <div className="relative">
                      <select
                        value={(data.deceased_spouse_birth_date && data.deceased_spouse_birth_date.month) || ''}
                        onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_birth_date.month', e.target.value)}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                      >
                        <option value="">选择月份</option>
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                    <div className="relative">
                      <select
                        value={(data.deceased_spouse_birth_date && data.deceased_spouse_birth_date.day) || ''}
                        onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_birth_date.day', e.target.value)}
                        disabled={!data.deceased_spouse_birth_date?.year || !data.deceased_spouse_birth_date?.month}
                        className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <option value="">选择日期</option>
                        {generateDayOptions(data.deceased_spouse_birth_date?.year, data.deceased_spouse_birth_date?.month).map(day => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 已故配偶国籍 */}
              <div>
                <CountrySelect
                  label="已故配偶国籍"
                  value={data.deceased_spouse_nationality || ''}
                  onChange={(value) => updateDeceasedSpouseInfo('deceased_spouse_nationality', value)}
                  required
                />
              </div>

              {/* 已故配偶出生地 */}
              <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border-l-4 border-violet-400">
                <h5 className="text-md font-semibold text-gray-700 flex items-center mb-4">
                  <MapPin className="w-4 h-4 mr-2" />
                  已故配偶出生地
                </h5>

                <div className="space-y-4">
                  <FormField
                    label="出生城市"
                    value={data.deceased_spouse_pob_city || ''}
                    onChange={(e) => updateDeceasedSpouseInfo('deceased_spouse_pob_city', e.target.value)}
                    placeholder="请输入出生城市"
                    required
                  />

                  <CountrySelect
                    label="出生国家/地区"
                    value={data.deceased_spouse_pob_country || ''}
                    onChange={(value) => updateDeceasedSpouseInfo('deceased_spouse_pob_country', value)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 预览信息 */}
        {(data.father_surnames || data.mother_surnames || data.spouse_surnames || data.former_spouses || data.deceased_spouse_surnames) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border-l-4 border-pink-400 shadow-sm">
            <h4 className="font-semibold text-pink-800 mb-2">信息预览</h4>
            <div className="text-sm text-pink-700 space-y-1">
              {/* 父亲信息预览 */}
              {(data.father_surnames || data.father_surnames_unk_ind) && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-blue-300 mb-2">
                  <p><strong>👨 父亲:</strong>
                    {data.father_surnames_unk_ind ? (
                      <span className="text-orange-600">姓氏未知</span>
                    ) : (
                      data.father_surnames
                    )}
                    {' '}
                    {data.father_given_name_unk_ind ? (
                      <span className="text-orange-600">名字未知</span>
                    ) : (
                      data.father_given_name
                    )}
                  </p>
                  {data.father_dob_unk_ind ? (
                    <p><strong>出生日期:</strong> <span className="text-orange-600">未知</span></p>
                  ) : data.father_birth_date && data.father_birth_date.year ? (
                    <p><strong>出生日期:</strong> {data.father_birth_date.year}-{data.father_birth_date.month}-{data.father_birth_date.day}</p>
                  ) : null}
                  <p><strong>居住在美国:</strong> {data.father_live_in_us_ind ? '✅ 是' : '❌ 否'}</p>
                  {data.father_live_in_us_ind && data.if_father_in_us_status && (
                    <p><strong>美国身份:</strong> {getStatusLabel(data.if_father_in_us_status)}</p>
                  )}
                </div>
              )}

              {/* 母亲信息预览 */}
              {(data.mother_surnames || data.mother_surnames_unk_ind) && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-purple-300 mb-2">
                  <p><strong>👩 母亲:</strong>
                    {data.mother_surnames_unk_ind ? (
                      <span className="text-orange-600">姓氏未知</span>
                    ) : (
                      data.mother_surnames
                    )}
                    {' '}
                    {data.mother_given_name_unk_ind ? (
                      <span className="text-orange-600">名字未知</span>
                    ) : (
                      data.mother_given_name
                    )}
                  </p>
                  {data.mother_dob_unk_ind ? (
                    <p><strong>出生日期:</strong> <span className="text-orange-600">未知</span></p>
                  ) : data.mother_birth_date && data.mother_birth_date.year ? (
                    <p><strong>出生日期:</strong> {data.mother_birth_date.year}-{data.mother_birth_date.month}-{data.mother_birth_date.day}</p>
                  ) : null}
                  <p><strong>居住在美国:</strong> {data.mother_live_in_us_ind ? '✅ 是' : '❌ 否'}</p>
                  {data.mother_live_in_us_ind && data.if_mother_in_us_status && (
                    <p><strong>美国身份:</strong> {getStatusLabel(data.if_mother_in_us_status)}</p>
                  )}
                </div>
              )}

              {/* 其他美国亲属信息预览 */}
              {data.us_immed_relative_ind !== undefined && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-emerald-300 mb-2">
                  <p><strong>🏠 其他美国亲属:</strong> {data.us_immed_relative_ind ? '✅ 有其他直系亲属在美国' : '❌ 没有其他直系亲属在美国'}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    <strong>直系亲属包括：</strong> 未婚夫/妻、配偶、子女、兄弟姐妹
                  </p>
                </div>
              )}

              {/* 其他亲属信息预览 */}
              {data.us_other_relative_ind !== undefined && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-blue-300 mb-2">
                  <p><strong>👥 其他亲属:</strong> {data.us_other_relative_ind ? '✅ 有其他亲属在美国' : '❌ 没有其他亲属在美国'}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    <strong>其他亲属包括：</strong> 堂兄弟姐妹、表兄弟姐妹、叔叔阿姨、祖父母等
                  </p>
                </div>
              )}

              {/* 配偶信息预览 */}
              {data.spouse_surnames && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-rose-300">
                  <p><strong>💕 配偶:</strong> {data.spouse_surnames} {data.spouse_given_name}</p>
                  {data.spouse_dob_unk_ind ? (
                    <p><strong>出生日期:</strong> <span className="text-orange-600">未知</span></p>
                  ) : data.spouse_birth_date && data.spouse_birth_date.year ? (
                    <p><strong>出生日期:</strong> {data.spouse_birth_date.year}-{data.spouse_birth_date.month}-{data.spouse_birth_date.day}</p>
                  ) : null}
                  {data.spouse_nationality && (
                    <p><strong>国籍:</strong> {data.spouse_nationality}</p>
                  )}

                  {/* 出生地信息 */}
                  {(data.spouse_pob_city || data.spouse_pob_country) && (
                    <p><strong>出生地:</strong>
                      {data.spouse_pob_city_unk_ind ? (
                        <span className="text-orange-600">城市未知</span>
                      ) : data.spouse_pob_city ? (
                        data.spouse_pob_city
                      ) : null}
                      {data.spouse_pob_city && data.spouse_pob_country && ', '}
                      {data.spouse_pob_country}
                    </p>
                  )}

                  {/* 地址信息 */}
                  {data.spouse_address_type && (
                    <p><strong>地址:</strong>
                      {data.spouse_address_type === 'O' ? (
                        data.spouse_address && data.spouse_address.street_address ? (
                          `${data.spouse_address.street_address}${data.spouse_address.city ? ', ' + data.spouse_address.city : ''}${data.spouse_address.country ? ', ' + data.spouse_address.country : ''}`
                        ) : '具体地址待填写'
                      ) : (
                        {
                          'H': '与家庭住址相同',
                          'M': '与邮寄地址相同',
                          'U': '与美国联系人地址相同',
                          'D': '不知道'
                        }[data.spouse_address_type] || data.spouse_address_type
                      )}
                    </p>
                  )}

                  <p><strong>居住在美国:</strong> {data.spouse_live_in_us_ind ? '✅ 是' : '❌ 否'}</p>
                  {data.spouse_live_in_us_ind && data.if_spouse_in_us_status && (
                    <p><strong>美国身份:</strong> {getStatusLabel(data.if_spouse_in_us_status)}</p>
                  )}
                  <div className="mt-2 text-xs bg-rose-50 text-rose-700 px-2 py-1 rounded">
                    婚姻状况: {MARITAL_STATUS_LABELS[maritalStatus]}
                  </div>
                </div>
              )}

              {/* 前配偶信息预览 */}
              {data.former_spouses && data.former_spouses.length > 0 && (
                <div className="space-y-3">
                  <div className="p-3 bg-white rounded-lg border-l-2 border-orange-300">
                    <p><strong>👫 前配偶信息:</strong></p>
                    <p><strong>前配偶数量:</strong> {data.former_spouses.length} 人</p>

                    {data.former_spouses.map((formerSpouse, index) => (
                      <div key={index} className="mt-3 p-3 bg-orange-50 rounded-lg border-l-2 border-orange-400">
                        <p><strong>前配偶 #{index + 1}:</strong> {formerSpouse.surnames} {formerSpouse.given_name}</p>

                        {/* 出生日期 */}
                        {formerSpouse.birth_date && formerSpouse.birth_date.year && (
                          <p><strong>出生日期:</strong> {formerSpouse.birth_date.year}-{formerSpouse.birth_date.month}-{formerSpouse.birth_date.day}</p>
                        )}

                        {/* 国籍 */}
                        {formerSpouse.nationality && (
                          <p><strong>国籍:</strong> {formerSpouse.nationality}</p>
                        )}

                        {/* 出生地 */}
                        {(formerSpouse.pob_city || formerSpouse.pob_country) && (
                          <p><strong>出生地:</strong>
                            {formerSpouse.pob_city_unk_ind ? (
                              <span className="text-orange-600">城市未知</span>
                            ) : formerSpouse.pob_city ? (
                              formerSpouse.pob_city
                            ) : null}
                            {formerSpouse.pob_city && formerSpouse.pob_country && ', '}
                            {formerSpouse.pob_country}
                          </p>
                        )}

                        {/* 结婚日期 */}
                        {formerSpouse.marriage_date && formerSpouse.marriage_date.year && (
                          <p><strong>结婚日期:</strong> {formerSpouse.marriage_date.year}-{formerSpouse.marriage_date.month}-{formerSpouse.marriage_date.day}</p>
                        )}

                        {/* 婚姻结束日期 */}
                        {formerSpouse.marriage_end_date && formerSpouse.marriage_end_date.year && (
                          <p><strong>婚姻结束日期:</strong> {formerSpouse.marriage_end_date.year}-{formerSpouse.marriage_end_date.month}-{formerSpouse.marriage_end_date.day}</p>
                        )}

                        {/* 婚姻如何结束 */}
                        {formerSpouse.how_marriage_ended && (
                          <p><strong>婚姻结束方式:</strong> {formerSpouse.how_marriage_ended.length > 50 ? formerSpouse.how_marriage_ended.substring(0, 50) + '...' : formerSpouse.how_marriage_ended}</p>
                        )}

                        {/* 婚姻结束国家 */}
                        {formerSpouse.marriage_end_country && (
                          <p><strong>婚姻结束国家:</strong> {formerSpouse.marriage_end_country}</p>
                        )}
                      </div>
                    ))}

                    <div className="mt-2 text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">
                      婚姻状况: {MARITAL_STATUS_LABELS[maritalStatus]}
                    </div>
                  </div>
                </div>
              )}

              {/* 已故配偶信息预览 */}
              {data.deceased_spouse_surnames && (
                <div className="p-3 bg-white rounded-lg border-l-2 border-purple-300 mb-2">
                  <p><strong>💜 已故配偶:</strong> {data.deceased_spouse_surnames} {data.deceased_spouse_given_name}</p>

                  {/* 出生日期 */}
                  {data.deceased_spouse_birth_date && data.deceased_spouse_birth_date.year && (
                    <p><strong>出生日期:</strong> {data.deceased_spouse_birth_date.year}-{data.deceased_spouse_birth_date.month}-{data.deceased_spouse_birth_date.day}</p>
                  )}

                  {/* 国籍 */}
                  {data.deceased_spouse_nationality && (
                    <p><strong>国籍:</strong> {data.deceased_spouse_nationality}</p>
                  )}

                  {/* 出生地 */}
                  {(data.deceased_spouse_pob_city || data.deceased_spouse_pob_country) && (
                    <p><strong>出生地:</strong>
                      {data.deceased_spouse_pob_city}
                      {data.deceased_spouse_pob_city && data.deceased_spouse_pob_country && ', '}
                      {data.deceased_spouse_pob_country}
                    </p>
                  )}

                  <div className="mt-2 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                    婚姻状况: {MARITAL_STATUS_LABELS[maritalStatus]}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FamilyInfoForm;
